import {decrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import type {ConnectionMutationVariables} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {type UseMutationResult, useQueryClient} from '@github-ui/react-query'
import {deleteIssueMutation as deleteIssueMutationQueryId} from '@github-ui/server-persisted-queries'
import type {DeleteIssueMutationMutation} from '@github-ui/server-persisted-queries/types/deleteIssueMutation'

import type {IssueDeletionConnection} from '../components/OptionConfig'
import type {IssueDeletionResult} from '../hooks/use-issue-deletion-state'
import {issueLifecyclePath, submitIssueLifecycleMutation} from './issue-lifecycle-mutation'

export function useDeleteIssueMutation({
  owner,
  repository,
  number,
  issueId,
  issueDeletionConnection,
  onCompleted,
  onError,
  onCompletionError,
}: {
  owner: string
  repository: string
  number: number
  issueId: string
  issueDeletionConnection?: IssueDeletionConnection
  onCompleted?: (issueId: string, options: {alreadySettled: boolean}) => void | Promise<void>
  onError?: (error: Error) => void
  onCompletionError?: (error: Error) => void
}): UseMutationResult<IssueDeletionResult, Error, ConnectionMutationVariables> {
  const ownerId = issueDeletionConnection?.ownerId ?? issueId
  const connectionPath = issueDeletionConnection?.connectionPath ?? []
  const queryClient = useQueryClient()

  return useConnectionMutation<IssueDeletionResult>({
    ownerId,
    connectionPath,
    prepareVariables: variables => ({
      ...variables,
      issueDeletion: {issueId, owner, repository, number},
    }),
    mutationFn: async (_operation, variables) => {
      const data = await submitDeleteIssueMutation({owner, repository, number})
      const deletedIssueId = data.deleteIssue?.issue?.id
      if (!deletedIssueId) throw new Error('Issue deletion failed')
      const settle = async () => {
        if (!issueDeletionConnection) return
        const operation =
          variables.operation.type === 'removeFromConnection'
            ? {...variables.operation, nodeId: deletedIssueId}
            : {
                type: 'removeFromConnection' as const,
                connectionPath: issueDeletionConnection.connectionPath,
                nodeId: deletedIssueId,
              }
        const stableTargets = issueDeletionConnection.targets
        let targets = stableTargets
        let targetResolutionError: Error | undefined
        try {
          targets = issueDeletionConnection.getTargets?.() ?? targets
        } catch (error) {
          targetResolutionError =
            error instanceof Error ? error : new Error('Issue deletion target resolution failed', {cause: error})
        }
        const settleTargets = async (settlementTargets: typeof targets) => {
          await settleConfirmedOperation({
            queryClient,
            targets: settlementTargets,
            ownerId: issueDeletionConnection.ownerId,
            operation,
            operationId: variables.operationId ?? variables.mutationId,
          })
        }
        try {
          await settleTargets(targets)
        } catch (error) {
          const settlementError =
            error instanceof Error ? error : new Error('Issue deletion settlement failed', {cause: error})
          if (targets !== stableTargets) {
            try {
              await settleTargets(stableTargets)
            } catch (fallbackError) {
              const fallbackSettlementError =
                fallbackError instanceof Error
                  ? fallbackError
                  : new Error('Issue deletion fallback settlement failed', {cause: fallbackError})
              throw new AggregateError(
                [targetResolutionError, settlementError, fallbackSettlementError].filter(
                  (completionError): completionError is Error => completionError !== undefined,
                ),
                'Issue deletion settlement failed',
              )
            }
          }
          if (!targetResolutionError) throw settlementError
          throw new AggregateError(
            [targetResolutionError, settlementError],
            'Issue deletion target resolution and settlement failed',
          )
        }
        if (targetResolutionError) throw targetResolutionError
      }

      const settleAfterOnCompleted = issueDeletionConnection?.settleAfterOnCompleted === true
      const completionErrors: Error[] = []
      let alreadySettled = false
      const captureCompletionError = async (callback: () => void | Promise<void>) => {
        try {
          await callback()
        } catch (error) {
          completionErrors.push(
            error instanceof Error ? error : new Error('Issue deletion completion failed', {cause: error}),
          )
        }
      }

      if (!settleAfterOnCompleted) {
        await captureCompletionError(async () => {
          await settle()
          alreadySettled = true
        })
      }
      await captureCompletionError(() => onCompleted?.(deletedIssueId, {alreadySettled}))
      if (settleAfterOnCompleted) await captureCompletionError(settle)
      decrementIssuesTabCounter()

      const firstError = completionErrors[0]
      let completionError: Error | undefined
      if (firstError) {
        const error =
          completionErrors.length === 1
            ? firstError
            : new AggregateError(completionErrors, 'Issue deletion completed, but reconciliation failed')
        completionError = error
        await captureCompletionError(() => onCompletionError?.(error))
      }
      return {issueId: deletedIssueId, completionError}
    },
    onError,
  })
}

export function submitDeleteIssueMutation({
  owner,
  repository,
  number,
}: {
  owner: string
  repository: string
  number: number
}) {
  return submitIssueLifecycleMutation<DeleteIssueMutationMutation>({
    path: `${issueLifecyclePath(owner, repository, number)}.json`,
    method: 'DELETE',
    queryId: deleteIssueMutationQueryId,
    errorMessage: 'Issue deletion failed',
    isSuccess: data => data.deleteIssue?.issue != null,
  })
}
